import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Minus,
  Lightbulb,
  Sparkles,
  Info,
  ArrowUpRight,
  Instagram,
  Youtube,
  Layers,
  Heart,
  MessageCircle,
  ExternalLink,
  Video,
  Image as ImageIcon,
  Users,
  Eye,
  Clock,
  Share2,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Loader, ErrorState, EmptyState } from "@/components/states";

const number = (value) => (value == null ? "—" : Number(value).toLocaleString());
const signed = (value) => {
  if (value == null) return "—";
  const n = Number(value);
  const formatted = Math.abs(n).toLocaleString();
  if (n > 0) return `+${formatted}`;
  if (n < 0) return `-${formatted}`;
  return formatted;
};

function MetricCard({ label, value, icon: Icon, tone = "default", subtitle = null, testid }) {
  const toneClass =
    tone === "positive" ? "text-accent" :
    tone === "negative" ? "text-destructive" : "text-foreground";
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-surface p-4 shadow-sm transition-all hover:border-accent/30 hover:shadow-md sm:p-5"
      data-testid={testid}>
      <div className="flex items-start justify-between gap-1">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground sm:text-xs">{label}</p>
        {Icon && <Icon className="h-4 w-4 shrink-0 text-muted-foreground/50" />}
      </div>
      <p className={`mt-2 font-display text-xl font-bold sm:text-2xl ${toneClass}`}>{value}</p>
      {subtitle && <p className="mt-1 text-[10px] text-muted-foreground sm:text-xs">{subtitle}</p>}
    </div>
  );
}

function TrendBadge({ direction, delta }) {
  if (direction === "above") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-semibold text-accent">
        <TrendingUp className="h-3 w-3" />
        {delta != null ? `+${delta}%` : "Above baseline"}
      </span>
    );
  }
  if (direction === "below") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2.5 py-0.5 text-xs font-semibold text-destructive">
        <TrendingDown className="h-3 w-3" />
        {delta != null ? `${delta}%` : "Below baseline"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted/30 px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
      <Minus className="h-3 w-3" />
      In line
    </span>
  );
}

export default function Analytics() {
  const [state, setState] = useState({ loading: true, error: null });
  const [data, setData] = useState({ overview: null, timeseries: null, sync: null, learnings: null });
  const [activeTab, setActiveTab] = useState("all");
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async (tab = activeTab) => {
    setState((prev) => ({ ...prev, loading: !prev.overview }));
    try {
      const platParam = tab === "all" ? "" : `?platform=${tab}`;
      const [overview, timeseries, sync, learnings] = await Promise.all([
        api.get(`/analytics/overview${platParam}`),
        api.get(`/analytics/timeseries${platParam}`),
        api.get("/analytics/sync/status"),
        api.get("/analytics/learnings").catch(() => ({ data: null })),
      ]);
      setData({
        overview: overview.data,
        timeseries: timeseries.data,
        sync: sync.data,
        learnings: learnings.data,
      });
      setState({ loading: false, error: null });
    } catch (error) {
      setState({ loading: false, error: apiErrorMessage(error) });
    }
  }, [activeTab]);

  useEffect(() => {
    load(activeTab);
  }, [load, activeTab]);

  const sync = async () => {
    setSyncing(true);
    try {
      const platParam = activeTab === "all" ? "" : `?platform=${activeTab}`;
      const res = await api.post(`/analytics/sync${platParam}`);
      const persisted = res?.data?.snapshots_persisted ?? 0;
      toast.success(
        persisted > 0
          ? `Synced ${persisted} item${persisted === 1 ? "" : "s"} across connected platforms`
          : "Analytics updated successfully"
      );
      await load(activeTab);
    } catch (error) {
      const status = error?.response?.status;
      const message = apiErrorMessage(error);
      if (status === 401) {
        toast.error("Account authorization was revoked. Reconnect your channel in Connections.");
      } else {
        toast.error(message);
      }
      await load(activeTab);
    } finally {
      setSyncing(false);
    }
  };

  const chartData = useMemo(() => {
    const rows = data.timeseries?.data || [];
    return rows.map((row) => ({
      date: row.captured_at ? String(row.captured_at).slice(0, 10) : "",
      views: row.views ?? 0,
      likes: row.likes ?? 0,
      comments: row.comments ?? 0,
      watch_time_minutes: row.watch_time_minutes ?? 0,
    }));
  }, [data.timeseries]);

  if (state.loading) return <AppShell title="Analytics"><Loader label="Loading real analytics" /></AppShell>;
  if (state.error) return <AppShell title="Analytics"><ErrorState message={state.error} onRetry={() => load(activeTab)} /></AppShell>;

  const configured = Boolean(data.sync?.configured);
  const connectedPlatforms = data.sync?.platforms || [];
  const hasData = Boolean(data.overview?.data?.length || (data.overview?.posts && data.overview.posts.length > 0));
  const metrics = data.overview?.metrics || {};
  const byPlatform = data.overview?.by_platform || {};
  const learnings = data.learnings;
  const posts = data.overview?.posts || [];

  const ytConnected = connectedPlatforms.find((p) => p.platform === "youtube");
  const igConnected = connectedPlatforms.find((p) => p.platform === "instagram");

  const ytMetrics = byPlatform.youtube?.metrics || {};
  const igMetrics = byPlatform.instagram?.metrics || {};
  const igPosts = posts.filter((p) => p.platform === "instagram");

  const netSubscribers =
    metrics.subscribers_gained != null || metrics.subscribers_lost != null
      ? (metrics.subscribers_gained || 0) - (metrics.subscribers_lost || 0)
      : null;

  return (
    <AppShell title="Analytics">
      {/* Top Header */}
      <div className="mb-8 flex flex-col gap-4 border-b border-border pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">Platform Intelligence</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-foreground" data-testid="analytics-page-title">Analytics</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Ranked by your Creator DNA and real evidence.
          </p>
        </div>

        {configured && (
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={sync} disabled={syncing} data-testid="analytics-refresh-button">
              <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Refresh Analytics"}
            </Button>
          </div>
        )}
      </div>

      {!configured ? (
        <EmptyState
          icon={BarChart3}
          testid="analytics-no-connection"
          title="No creator accounts connected"
          description="Connect your YouTube or Instagram accounts in Connections to begin collecting real analytics and unlocking creator learnings."
        />
      ) : !hasData ? (
        <EmptyState
          icon={BarChart3}
          testid="analytics-no-data"
          title="Platforms connected, but no performance data synced yet"
          description="Click Refresh Analytics to pull the latest posts, metrics, and insights from your connected platforms."
        />
      ) : (
        <div className="space-y-8" data-testid="analytics-real-data">
          {/* Platform Navigation Tabs */}
          <div className="flex flex-col gap-3 border-b border-border pb-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface p-1.5 shadow-sm">
              <button
                type="button"
                onClick={() => setActiveTab("all")}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all ${
                  activeTab === "all"
                    ? "bg-accent text-accent-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Layers className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">All Platforms</span>
                <span className="sm:hidden">All</span>
              </button>

              {ytConnected && (
                <button
                  type="button"
                  onClick={() => setActiveTab("youtube")}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all ${
                    activeTab === "youtube"
                      ? "bg-[#FF0000] text-white shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Youtube className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">YouTube Analytics</span>
                  <span className="sm:hidden">YouTube</span>
                </button>
              )}

              {igConnected && (
                <button
                  type="button"
                  onClick={() => setActiveTab("instagram")}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all ${
                    activeTab === "instagram"
                      ? "bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] text-white shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Instagram className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">Instagram Analytics</span>
                  <span className="sm:hidden">Instagram</span>
                </button>
              )}
            </div>

            {data.sync.last_synced_at && (
              <span className="text-xs text-muted-foreground">
                Last synced {new Date(data.sync.last_synced_at).toLocaleString()}
              </span>
            )}
          </div>

          {/* ========================================================================= */}
          {/* TAB 1: ALL PLATFORMS COMPARATIVE OVERVIEW                                */}
          {/* ========================================================================= */}
          {activeTab === "all" && (
            <div className="space-y-8">
              {/* Separate Platform Cards (Side-by-Side Summary) */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* YouTube Summary Card */}
                {ytConnected && (
                  <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
                    <div className="flex items-center justify-between border-b border-border pb-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#FF0000]/15 text-[#FF0000]">
                          <Youtube className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-display text-base font-bold text-foreground">YouTube Channel</h3>
                          <p className="text-xs text-muted-foreground">{ytConnected.account_name || ytConnected.account_id}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setActiveTab("youtube")}
                        className="flex items-center gap-1 text-xs font-semibold text-accent hover:underline"
                      >
                        Deep Dive <ArrowUpRight className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Views</p>
                        <p className="mt-1 font-display text-xl font-bold text-foreground">{number(ytMetrics.views)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Watch Time</p>
                        <p className="mt-1 font-display text-xl font-bold text-foreground">
                          {number(Math.round((ytMetrics.watch_time_minutes || 0) / 60))} hrs
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Subscribers</p>
                        <p className="mt-1 font-display text-xl font-bold text-accent">
                          {signed((ytMetrics.subscribers_gained || 0) - (ytMetrics.subscribers_lost || 0))}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Likes</p>
                        <p className="mt-1 font-display text-xl font-bold text-foreground">{number(ytMetrics.likes)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Comments</p>
                        <p className="mt-1 font-display text-xl font-bold text-foreground">{number(ytMetrics.comments)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Engagement</p>
                        <p className="mt-1 font-display text-xl font-bold text-accent">
                          {ytMetrics.engagement_rate != null ? `${ytMetrics.engagement_rate}%` : "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Instagram Summary Card */}
                {igConnected && (
                  <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
                    <div className="flex items-center justify-between border-b border-border pb-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#E1306C]/15 text-[#E1306C]">
                          <Instagram className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-display text-base font-bold text-foreground">Instagram Creator</h3>
                          <p className="text-xs text-muted-foreground">{igConnected.account_name || igConnected.account_id}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setActiveTab("instagram")}
                        className="flex items-center gap-1 text-xs font-semibold text-accent hover:underline"
                      >
                        Deep Dive <ArrowUpRight className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Total Likes</p>
                        <p className="mt-1 font-display text-xl font-bold text-foreground">{number(igMetrics.likes)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Total Comments</p>
                        <p className="mt-1 font-display text-xl font-bold text-foreground">{number(igMetrics.comments)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Interactions</p>
                        <p className="mt-1 font-display text-xl font-bold text-accent">
                          {number((igMetrics.likes || 0) + (igMetrics.comments || 0))}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Followers</p>
                        <p className="mt-1 font-display text-xl font-bold text-foreground">
                          {number(igConnected.meta?.followers_count || igMetrics.subscribers_gained)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Media Synced</p>
                        <p className="mt-1 font-display text-xl font-bold text-foreground">
                          {number(igConnected.meta?.media_count || igPosts.length)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Engagement</p>
                        <p className="mt-1 font-display text-xl font-bold text-accent">
                          {igMetrics.engagement_rate != null ? `${igMetrics.engagement_rate}%` : "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Combined Top Metrics */}
              <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                <MetricCard label="Total Views & Reach" value={number(metrics.views)} icon={Eye} testid="analytics-total-views" />
                <MetricCard label="Total Likes" value={number(metrics.likes)} icon={Heart} testid="analytics-total-likes" />
                <MetricCard label="Total Comments" value={number(metrics.comments)} icon={MessageCircle} testid="analytics-total-comments" />
                <MetricCard
                  label="Combined Engagement"
                  value={metrics.engagement_rate != null ? `${metrics.engagement_rate}%` : "—"}
                  icon={Sparkles}
                  tone="positive"
                  testid="analytics-engagement-rate"
                />
              </div>

              {/* Cross-Platform Daily Trend Chart */}
              {chartData.length > 0 && (
                <section className="rounded-xl border border-border bg-surface p-6">
                  <h2 className="mb-5 font-display text-base font-semibold text-foreground">Cross-Platform Activity Trend</h2>
                  <div className="h-72 w-full min-w-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="allViewsGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0.0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                        <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} />
                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--surface))",
                            borderColor: "hsl(var(--border))",
                            borderRadius: "8px",
                            fontSize: "12px",
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="views"
                          name="Views / Reach"
                          stroke="hsl(var(--accent))"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#allViewsGrad)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </section>
              )}
            </div>
          )}

          {/* ========================================================================= */}
          {/* TAB 2: SEPARATE YOUTUBE STUDIO ANALYTICS                                  */}
          {/* ========================================================================= */}
          {activeTab === "youtube" && (
            <div className="space-y-8">
              {/* YouTube Header Banner */}
              <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#FF0000]/30 bg-[#FF0000]/5 p-4 sm:p-5">
                <div className="flex flex-1 items-center gap-3.5 min-w-0">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#FF0000] text-white shadow-md">
                    <Youtube className="h-6 w-6" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate font-display text-base font-bold text-foreground sm:text-lg">
                      {ytConnected?.account_name || "YouTube Creator Channel"}
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Channel ID: {ytConnected?.account_id || "Connected"} · Latest snapshot: {ytConnected?.latest_snapshot_date || "Synced"}
                    </p>
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-[#FF0000]/15 px-3 py-1 text-xs font-semibold text-[#FF0000]">
                  YouTube Official Data
                </span>
              </div>

              {/* YouTube Specific Metric Cards */}
              <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
                <MetricCard label="Total Views" value={number(metrics.views)} icon={Eye} testid="analytics-yt-views" />
                <MetricCard
                  label="Watch Time (Hours)"
                  value={number(Math.round((metrics.watch_time_minutes || 0) / 60))}
                  subtitle={`${number(metrics.watch_time_minutes)} total minutes`}
                  icon={Clock}
                  testid="analytics-yt-watchtime"
                />
                <MetricCard
                  label="Net Subscribers"
                  value={signed(netSubscribers)}
                  tone={netSubscribers != null && netSubscribers < 0 ? "negative" : "positive"}
                  icon={Users}
                  testid="analytics-yt-subs"
                />
                <MetricCard label="Total Likes" value={number(metrics.likes)} icon={Heart} testid="analytics-yt-likes" />
                <MetricCard label="Total Comments" value={number(metrics.comments)} icon={MessageCircle} testid="analytics-yt-comments" />
              </div>

              {/* YouTube 28-day Deterministic Learnings */}
              {learnings && learnings.reason === "ok" && learnings.trend && (
                <section className="rounded-xl border border-border bg-gradient-to-br from-surface via-surface to-elevated p-6 shadow-sm">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/15 text-accent">
                        <Lightbulb className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-display text-base font-bold text-foreground">
                          YouTube Channel Learnings
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Deterministic insights computed from your channel's 28-day history
                        </p>
                      </div>
                    </div>
                    {learnings?.window_days && (
                      <span className="text-xs text-muted-foreground">
                        Window: past {learnings.window_days} days
                      </span>
                    )}
                  </div>

                  <div className="mt-5 space-y-4">
                    <div className="rounded-lg border border-accent/25 bg-accent/5 p-4">
                      <ul className="space-y-2 text-sm text-foreground">
                        {(learnings.learnings || []).map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* 14-day Comparison Grid */}
                    {learnings.metrics && (
                      <div className="grid gap-4 grid-cols-1 xs:grid-cols-3">
                        <div className="rounded-lg border border-border bg-surface p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Views</span>
                            <TrendBadge direction={learnings.trend?.views_direction} delta={learnings.trend?.views_delta_pct} />
                          </div>
                          <p className="mt-2 text-xl font-bold font-display text-foreground">{number(learnings.metrics.recent_views)}</p>
                          <p className="mt-1 text-xs text-muted-foreground">vs {number(learnings.metrics.prior_views)} prior window</p>
                        </div>

                        <div className="rounded-lg border border-border bg-surface p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Engagement</span>
                            <TrendBadge direction={learnings.trend?.engagement_direction} delta={learnings.trend?.engagement_delta_pct} />
                          </div>
                          <p className="mt-2 text-xl font-bold font-display text-foreground">
                            {learnings.metrics.recent_engagement_rate != null ? `${learnings.metrics.recent_engagement_rate}%` : "—"}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">vs {learnings.metrics.prior_engagement_rate != null ? `${learnings.metrics.prior_engagement_rate}%` : "—"} prior window</p>
                        </div>

                        <div className="rounded-lg border border-border bg-surface p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Subscribers</span>
                            <TrendBadge
                              direction={
                                (learnings.metrics.recent_net_subscribers || 0) > (learnings.metrics.prior_net_subscribers || 0)
                                  ? "above"
                                  : "below"
                              }
                              delta={learnings.trend?.subscribers_delta_pct}
                            />
                          </div>
                          <p className="mt-2 text-xl font-bold font-display text-foreground">{signed(learnings.metrics.recent_net_subscribers)}</p>
                          <p className="mt-1 text-xs text-muted-foreground">vs {signed(learnings.metrics.prior_net_subscribers)} prior window</p>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* YouTube Views Trend Area Chart */}
              {chartData.length > 0 && (
                <section className="rounded-xl border border-border bg-surface p-6">
                  <h2 className="mb-5 font-display text-base font-semibold text-foreground">YouTube Daily Views Velocity</h2>
                  <div className="h-72 w-full min-w-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="ytGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#FF0000" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#FF0000" stopOpacity={0.0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                        <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} />
                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--surface))",
                            borderColor: "hsl(var(--border))",
                            borderRadius: "8px",
                            fontSize: "12px",
                          }}
                        />
                        <Area type="monotone" dataKey="views" name="Views" stroke="#FF0000" strokeWidth={2} fillOpacity={1} fill="url(#ytGrad)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </section>
              )}
            </div>
          )}

          {/* ========================================================================= */}
          {/* TAB 3: SEPARATE INSTAGRAM CREATOR ANALYTICS                               */}
          {/* ========================================================================= */}
          {activeTab === "instagram" && (
            <div className="space-y-8">
              {/* Instagram Header Banner */}
              <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#E1306C]/30 bg-[#E1306C]/5 p-4 sm:p-5">
                <div className="flex flex-1 items-center gap-3.5 min-w-0">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] text-white shadow-md">
                    <Instagram className="h-6 w-6" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate font-display text-base font-bold text-foreground sm:text-lg">
                      @{igConnected?.account_name || "Instagram Creator"}
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Account Type: {igConnected?.meta?.account_type || "Media Creator"} · Synced Posts: {igPosts.length}
                    </p>
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-[#E1306C]/15 px-3 py-1 text-xs font-semibold text-[#E1306C]">
                  Instagram Live Data
                </span>
              </div>

              {/* Instagram Specific Metric Cards */}
              <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                <MetricCard label="Total Likes" value={number(metrics.likes)} icon={Heart} testid="analytics-ig-likes" />
                <MetricCard label="Total Comments" value={number(metrics.comments)} icon={MessageCircle} testid="analytics-ig-comments" />
                <MetricCard
                  label="Total Post Interactions"
                  value={number((metrics.likes || 0) + (metrics.comments || 0))}
                  tone="positive"
                  icon={Sparkles}
                  testid="analytics-ig-interactions"
                />
                <MetricCard
                  label="Engagement Rate"
                  value={metrics.engagement_rate != null ? `${metrics.engagement_rate}%` : "—"}
                  tone="positive"
                  icon={TrendingUp}
                  testid="analytics-ig-rate"
                />
              </div>

              {/* Instagram Daily Interactions Chart */}
              {chartData.length > 0 && (
                <section className="rounded-xl border border-border bg-surface p-6">
                  <h2 className="mb-5 font-display text-base font-semibold text-foreground">Instagram Likes & Interactions Trend</h2>
                  <div className="h-72 w-full min-w-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="igGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#E1306C" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#E1306C" stopOpacity={0.0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                        <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} />
                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--surface))",
                            borderColor: "hsl(var(--border))",
                            borderRadius: "8px",
                            fontSize: "12px",
                          }}
                        />
                        <Area type="monotone" dataKey="likes" name="Likes" stroke="#E1306C" strokeWidth={2} fillOpacity={1} fill="url(#igGrad)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </section>
              )}

              {/* Synced Instagram Posts & Reels Gallery */}
              <section className="rounded-xl border border-border bg-surface p-6">
                <div className="mb-5 flex items-center justify-between border-b border-border pb-4">
                  <div className="flex items-center gap-2">
                    <Instagram className="h-5 w-5 text-[#E1306C]" />
                    <h3 className="font-display text-base font-bold text-foreground">Synced Instagram Reels & Posts</h3>
                  </div>
                  <span className="text-xs text-muted-foreground">{igPosts.length} posts loaded</span>
                </div>

                {igPosts.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    No Instagram posts found yet. Click <strong>Refresh Analytics</strong> to pull recent Reels & Posts.
                  </p>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {igPosts.map((post) => (
                      <div
                        key={post.id}
                        className="group flex flex-col justify-between rounded-xl border border-border bg-elevated/40 p-4 transition-all hover:border-[#E1306C]/40 hover:shadow-md"
                      >
                        <div>
                          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2.5">
                            <span className="inline-flex items-center gap-1 rounded-md bg-surface px-2 py-0.5 font-medium uppercase text-[10px]">
                              {post.media_type === "VIDEO" ? <Video className="h-3 w-3 text-accent" /> : <ImageIcon className="h-3 w-3 text-muted-foreground" />}
                              {post.media_type || "POST"}
                            </span>
                            {post.published_at && <span>{String(post.published_at).slice(0, 10)}</span>}
                          </div>

                          <p className="line-clamp-3 text-sm text-foreground leading-snug font-normal mb-4">
                            {post.caption || "No caption provided"}
                          </p>
                        </div>

                        <div className="flex items-center justify-between border-t border-border/60 pt-3">
                          <div className="flex items-center gap-3 text-xs font-semibold text-muted-foreground">
                            <span className="flex items-center gap-1 text-[#E1306C]">
                              <Heart className="h-3.5 w-3.5 fill-current" /> {number(post.likes)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageCircle className="h-3.5 w-3.5" /> {number(post.comments)}
                            </span>
                          </div>

                          {post.permalink && (
                            <a
                              href={post.permalink}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center gap-1 text-xs font-semibold text-accent transition-colors hover:underline"
                            >
                              Open <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
